import React from "react";
import cx from "classnames";
import ReactCountryFlag from "react-country-flag";
import { AsYouType, CountryCode } from "libphonenumber-js/min";
import { CaretDown } from "components/icons/CaretDown";
import { Search } from "components/DesignSystem/Search/Search";
import rawCountries from "components/DesignSystem/MobileInput/rawCountries";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import BareInput, {
  BareInputProps,
  InputSize,
  InputVariant,
  textSizeVariants,
  flagSizeVariants,
} from "../Input/BareInput";

export interface CountryDropdownOption {
  value: CountryCode;
  label: string;
  triggerText: string;
}

export interface MobileInputProps
  extends Omit<BareInputProps, "prefix" | "leftComponent" | "onChange"> {
  countryCode?: CountryCode;
  onCountryChange?: (option: CountryDropdownOption) => void;
  onChange?: (
    e: React.ChangeEvent<HTMLInputElement>,
    selectedCountry: CountryDropdownOption
  ) => void;
}

const SUGGESTED_COUNTRY_CODES: CountryCode[] = [
  "US",
  "GB",
  "IN",
] as CountryCode[];

export const mobileOptions: CountryDropdownOption[] = rawCountries.map((c) => ({
  value: (c[2] as string).toUpperCase() as CountryCode,
  label: `${c[0] as string} (+${c[3]})`,
  triggerText: `+${c[3]}`,
}));

const suggestedOptions = SUGGESTED_COUNTRY_CODES.map((code) =>
  mobileOptions.find((o) => o.value === code)
).filter(Boolean) as CountryDropdownOption[];

const otherOptions = mobileOptions.filter(
  (o) => !SUGGESTED_COUNTRY_CODES.includes(o.value)
);

export const CountrySelectDropdown = ({
  selectedCountry,
  options,
  onSelect,
  disabled = false,
  variant,
  size = "regular",
  align = "start",
  dropdownWidth = "t-w-72",
  triggerClassName,
  allowCountrySelect = true,
}: {
  selectedCountry: CountryCode;
  options: CountryDropdownOption[];
  onSelect: (code: CountryCode) => void;
  disabled?: boolean;
  variant?: InputVariant;
  size?: InputSize;
  align?: "start" | "end";
  dropdownWidth?: string;
  triggerClassName?: string;
  allowCountrySelect?: boolean;
}) => {
  const [search, setSearch] = React.useState("");
  const isDisabled = disabled || variant === "loading";

  const isSearching = search.trim().length > 0;

  const normalizedSearch = search.toLowerCase();
  const matchesSearch = (o: CountryDropdownOption) =>
    o.label.toLowerCase().includes(normalizedSearch);

  const filteredSuggested = suggestedOptions.filter(matchesSearch);
  const filteredOthers = otherOptions.filter(matchesSearch);
  const flatFiltered = options.filter(matchesSearch);

  const selected = options.find((o) => o.value === selectedCountry);

  const renderItem = (opt: CountryDropdownOption) => (
    <Dropdown.Item
      key={opt.value}
      onSelect={() => onSelect(opt.value)}
      icon={
        <ReactCountryFlag countryCode={opt.value} svg className="t-w-4 t-h-3" />
      }
    >
      {opt.label}
    </Dropdown.Item>
  );

  return (
    <Dropdown.Root
      onOpenChange={(open) => {
        if (!open) setSearch("");
      }}
    >
      <Dropdown.Trigger asChild disabled={isDisabled || !allowCountrySelect}>
        <div
          className={cx(
            "t-flex t-items-center t-self-stretch t-text-text-30 t-cursor-pointer data-[disabled]:t-text-neutral-40 data-[disabled]:t-cursor-not-allowed t-group",
            textSizeVariants({ size }),
            triggerClassName
          )}
        >
          <div className="t-flex t-items-center t-gap-1">
            {selected && (
              <ReactCountryFlag
                countryCode={selected.value}
                svg
                className={flagSizeVariants({ size })}
              />
            )}
            <span>{selected?.triggerText}</span>
          </div>
          {allowCountrySelect && (
            <span className="t-pl-1 t-flex t-items-center">
              <CaretDown
                width={size === "small" ? 12 : 14}
                height={size === "small" ? 12 : 14}
                className="t-text-neutral-40 group-data-state-open:-t-rotate-180 t-transform t-transition t-duration-300 t-ease-in-out"
              />
            </span>
          )}
        </div>
      </Dropdown.Trigger>

      <Dropdown.Portal>
        <Dropdown.Content
          align={align}
          sideOffset={4}
          onCloseAutoFocus={(e) => e.preventDefault()}
          className={cx("!t-overflow-hidden !t-p-0", dropdownWidth)}
        >
          <div className="t-p-2 t-border-b t-border-neutral-10">
            <Search
              autoFocus
              block
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>
          <div className="t-max-h-48 t-overflow-y-auto t-p-1">
            {isSearching ? (
              <>
                {flatFiltered.map(renderItem)}
                {flatFiltered.length === 0 && (
                  <p className="t-text-body-sm t-text-neutral-40 t-px-2 t-py-2">
                    No results
                  </p>
                )}
              </>
            ) : (
              <>
                {filteredSuggested.length > 0 && (
                  <>
                    <Dropdown.Label className="!t-pt-0 !t-pr-3 !t-pb-1 !t-pl-3 t-text-neutral-40 t-text-body-sm t-font-light">
                      Suggested
                    </Dropdown.Label>
                    {filteredSuggested.map(renderItem)}
                  </>
                )}
                {filteredOthers.length > 0 && (
                  <>
                    <Dropdown.Label className="!t-pt-3 !t-pr-3 !t-pb-1 !t-pl-3 t-text-neutral-40 t-text-body-sm t-font-light">
                      All countries
                    </Dropdown.Label>
                    {filteredOthers.map(renderItem)}
                  </>
                )}
                {filteredSuggested.length === 0 &&
                  filteredOthers.length === 0 && (
                    <p className="t-text-body-sm t-text-neutral-40 t-px-2 t-py-2">
                      No results
                    </p>
                  )}
              </>
            )}
          </div>
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
};

const BareMobileInput = React.forwardRef<HTMLInputElement, MobileInputProps>(
  (
    {
      countryCode: countryCodeProp = "US" as CountryCode,
      onCountryChange,
      customSize = "regular",
      placeholder = "Enter phone number",
      onChange,
      disabled,
      variant: variantProp,
      ...props
    },
    ref
  ) => {
    const [selectedCountryCode, setSelectedCountryCode] =
      React.useState<CountryCode | null>(null);
    const countryCode = selectedCountryCode ?? countryCodeProp;

    const handleCountryChange = (code: CountryCode) => {
      const option = mobileOptions.find(({ value }) => value === code)!;
      setSelectedCountryCode(code);
      onCountryChange?.(option);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const formatter = new AsYouType(countryCode);
      e.target.value = formatter.input(e.target.value);
      const selectedCountry = mobileOptions.find(
        ({ value }) => value === countryCode
      )!;
      onChange?.(e, selectedCountry);
    };

    return (
      <BareInput
        ref={ref}
        type="tel"
        placeholder={placeholder}
        disabled={disabled}
        customSize={customSize}
        variant={variantProp}
        leftComponent={
          <CountrySelectDropdown
            selectedCountry={countryCode}
            options={mobileOptions}
            onSelect={handleCountryChange}
            disabled={disabled}
            variant={variantProp}
            size={customSize}
            align="start"
            dropdownWidth="t-w-72"
            triggerClassName="t-pr-0"
          />
        }
        onChange={handleChange}
        {...props}
      />
    );
  }
);

BareMobileInput.displayName = "MobileInput";

export default BareMobileInput;
